import {
  Radio,
  FormControlLabel,
  Typography,
  Box,
  Paper,
  Collapse,
  Grid,
  TextField,
  Autocomplete
} from '@mui/material'
import React, { useState, useEffect } from 'react'
import { Controller } from 'react-hook-form'
import countries from '../../../../../components/user-preferences/lpq/data/countries'
import CardsLogo from '../../../../../components/logos/cards.png'
import { ActionButton } from '../../../../../components'
import { useFormContext } from '../../../context'
import { CardCvvElement, CardMonthElement, CardNumberElement, CardYearElement, useRecurly, ThreeDSecureAction } from '@recurly/react-recurly'
import './recurly.css'
import { b2cSignupSlice } from '../../../../../reducers'
import { useDispatch, useSelector } from 'react-redux'
import { textFieldStyle } from '../styles'
import usePaymentEvent from '../../usePaymentEvent'
import { ERROR_CARD_PAYMENT_FAILED } from '../../const'
import useStyles from './styles'

export const CreditCard = ({ selectedValue, formRef }) => {
  const { paymentForm } = useFormContext()
  const classes = useStyles()
  const [allValid, setAllValid] = useState(false)
  const recurly = useRecurly()
  const coupon = useSelector(b2cSignupSlice.selectors.selectCoupon)
  const plan = useSelector(b2cSignupSlice.selectors.selectPlan)
  const currency = useSelector(b2cSignupSlice.selectors.selectCurrency)
  const [threeDSecureActionToken, setThreeDSecureActionToken] = useState(null)
  const paymentInfoSubmitted = useSelector(b2cSignupSlice.selectors.selectPaymentInfoSubmitted)
  const dispatch = useDispatch()
  const account = useSelector(b2cSignupSlice.selectors.selectAccount)
  const [recurlyFieldColor, setRecurlyFieldColor] = useState('black')
  const disabled = useSelector(b2cSignupSlice.selectors.selectDisabled)

  useEffect(() => {
    setRecurlyFieldColor(paymentInfoSubmitted ? 'rgba(0, 0, 0, 0.42)' : 'black')
  }, [paymentInfoSubmitted])

  const initiatePayment = usePaymentEvent({
    onSuccess: () => {
      dispatch(b2cSignupSlice.actions.setPaymentInfoSubmitted(false))
    },
    onError: (error) => {
      if (error.code === '3D_SECURE_REQUIRED') {
        dispatch(b2cSignupSlice.actions.setPaymentInfoSubmitted(true))
        return setThreeDSecureActionToken(error.description)
      }

      dispatch(b2cSignupSlice.actions.setPaymentInfoSubmitted(false))
    }
  })

  const [fieldsValid, setFieldsValid] = useState({
    number: false,
    month: false,
    year: false,
    cvv: false
  })

  const [errors, setErrors] = useState({
    number: { error: '', active: false, name: 'card number' },
    month: { error: '', active: false, name: 'month' },
    year: { error: '', active: false, name: 'year' },
    cvv: { error: '', active: false, name: 'cvv' }
  })

  useEffect(() => {
    const fields = paymentForm.formState.isValid && Object.values(fieldsValid).every(field => field === true)
    setAllValid(fields)
  }, [setAllValid, fieldsValid, paymentForm.formState, paymentForm])

  const handleChange = (field, event) => {
    dispatch(b2cSignupSlice.actions.clearPaymentError())
    const fields = {
      ...fieldsValid,
      [field]: event.valid && !event.empty
    }
    setFieldsValid(fields)

    // First interaction with field, no validation until blur
    if (!errors[field].interacted && (event.focus && event.length > 0)) {
      setErrors((prev) => ({
        ...prev,
        [field]: {
          ...prev[field],
          interacted: true
        }
      }))
      return
    }

    if (errors[field].interacted) {
      let message = ''
      if (!event.focus) {
        message = event.valid ? '' : event.empty ? `${errors[field].name} is required` : `${errors[field].name} is invalid`
      }
      setErrors((prev) => ({
        ...prev,
        [field]: {
          ...prev[field],
          error: message
        }
      }))
    }
  }

  const handleSubmit = (event) => {
    event.preventDefault()
    submitPayment()
  }

  const handleFieldChange = (field) => {
    return {
      ...field,
      onBlur (e) {
        field.onBlur(e)
        dispatch(b2cSignupSlice.actions.clearPaymentError())
      }
    }
  }

  const handle3DSecureToken = (token) => {
    submitPayment({
      threeDSecureActionResultToken: token.id
    })
  }

  const handle3DSecureError = () => {
    setThreeDSecureActionToken(null)
    dispatch(b2cSignupSlice.actions.setPaymentInfoSubmitted(false))
  }

  const renderOption = (props, option) => {
    const { key, ...others } = props

    return (
      <li {...others} key={key}>
        <Box display='flex' alignItems='baseline' flexDirection='row'>
          {option.label}
        </Box>
      </li>
    )
  }

  const submitPayment = async (event) => {
    dispatch(b2cSignupSlice.actions.setPaymentInfoSubmitted(true))

    recurly.token(formRef.current, (err, token) => {
      if (err) {
        dispatch(b2cSignupSlice.actions.setPaymentError(ERROR_CARD_PAYMENT_FAILED))
        dispatch(b2cSignupSlice.actions.setPaymentInfoSubmitted(false))
        //  Show Recurly card validation errors
        if (err.fields.length > 0) {
          setErrors((prev) => ({
            ...prev,
            [err.fields[0]]: {
              ...prev[err.fields[0]],
              error: err.message
            }
          }))
        }
      } else {
        const evtData = {
          planCode: plan?.code,
          currency,
          username: account.username,
          tokenId: token.id,
          paymentChannel: 'creditCard',
          couponCodes: coupon,
          firstName: account.firstName,
          lastName: account.lastName,
          email: account.email,
          subscriptionId: account?.subscription?.state !== 'expired' ? account?.subscription?.uuid : null
        }
        if (event?.threeDSecureActionResultToken) {
          evtData.threeDSecureActionResultTokenId = event?.threeDSecureActionResultToken
        }
        initiatePayment.trigger(evtData)
      }
    })
  }

  const getFieldBorder = (field) => {
    return { borderBottom: errors[field].error ? '1px solid red' : '1px solid rgba(0, 0, 0, 0.42)' }
  }

  return (
    <Paper variant='standard' sx={{ mb: 1, p: 2, width: '100%', borderRadius: '10px', border: '1px solid black' }}>
      {threeDSecureActionToken &&
        <ThreeDSecureAction
          actionTokenId={threeDSecureActionToken}
          onToken={handle3DSecureToken}
          onError={handle3DSecureError}
        />}
      <FormControlLabel
        value='creditCard'
        control={<Radio />}
        disabled={paymentInfoSubmitted || disabled}
        variant='standard'
        label={
          <Box display='flex' alignItems='center' justifyContent='space-between' width='100%'>
            <Typography sx={{ flexGrow: 1 }} variant='h6'>Credit/Debit</Typography>
            <img width='100' style={{ position: 'absolute', right: '24px' }} src={CardsLogo} alt='Credit cards' />
          </Box>
              }
        sx={{ width: '100%' }}
      />
      <Collapse in={selectedValue === 'creditCard'}>
        <>

          <Grid container spacing={1}>
            <Grid item xs={12}>
              <Box sx={getFieldBorder('number')}>
                <CardNumberElement
                  onChange={(event) => handleChange('number', event)}
                  className='recurly-input'
                  disabled={paymentInfoSubmitted}
                  style={{
                    fontColor: recurlyFieldColor,
                    borderBottom: errors.number ? '1px solid red' : '1px solid black',
                    background: 'transparent',
                    placeholder: {
                      content: 'Card number',
                      color: 'rgba(0, 0, 0, 0.42)'
                    }
                  }}
                />
              </Box>
              {errors.number && <p className={classes.errorMessage}>{errors.number.error}</p>}
            </Grid>
            <Grid item xs={6}>
              <Box sx={getFieldBorder('month')}>
                <CardMonthElement
                  onChange={(event) => handleChange('month', event)}
                  className='recurly-input'
                  disabled={paymentInfoSubmitted}
                  style={{
                    fontColor: recurlyFieldColor,
                    borderBottom: '1px solid black',
                    background: 'transparent',
                    placeholder: {
                      content: 'Month',
                      color: 'rgba(0, 0, 0, 0.42)'
                    }
                  }}
                />
              </Box>
              {errors.month.error && <p className={classes.errorMessage}>{errors.month.error}</p>}
            </Grid>
            <Grid item xs={6}>
              <Box sx={getFieldBorder('year')}>
                <CardYearElement
                  onChange={(event) => handleChange('year', event)}
                  className='recurly-input'
                  disabled={paymentInfoSubmitted}
                  style={{
                    fontColor: recurlyFieldColor,
                    borderBottom: '1px solid black',
                    background: 'transparent',
                    placeholder: {
                      content: 'Year',
                      color: 'rgba(0, 0, 0, 0.42)'
                    }
                  }}
                />
              </Box>
              {errors.year.error && <p className={classes.errorMessage}>{errors.year.error}</p>}
            </Grid>
            <Grid item xs={6}>
              <Box sx={getFieldBorder('cvv')}>
                <CardCvvElement
                  onChange={(event) => handleChange('cvv', event)}
                  className='recurly-input'
                  disabled={paymentInfoSubmitted}
                  style={{
                    fontColor: recurlyFieldColor,
                    borderBottom: '1px solid black',
                    background: 'transparent',
                    placeholder: {
                      content: 'CVV',
                      color: 'rgba(0, 0, 0, 0.42)'
                    }
                  }}
                />
              </Box>
              {errors.cvv.error && <p className={classes.errorMessage}>{errors.cvv.error}</p>}
            </Grid>
            <Grid item xs={6} mt={2} mb={1}>
              <Controller
                name='country'
                control={paymentForm.control}
                rules={{ required: 'Country is required' }}
                render={({ field: { onChange, value }, fieldState }) => (
                  <Autocomplete
                    value={countries.find((option) => option.label === value) || null}
                    onChange={(event, newValue) => {
                      onChange(newValue ? newValue.label : '')
                      paymentForm.trigger('country')// Force validation on change
                    }}
                    options={countries}
                    autoHighlight
                    renderOption={renderOption}
                    isOptionEqualToValue={(option, value) => option?.label === value.label}
                    getOptionLabel={(option) => option?.label || ''}
                    renderInput={params => (
                      <TextField
                        {...params}
                        placeholder='Country'
                        variant='standard'
                        fullWidth
                        required
                        sx={textFieldStyle}
                        error={!!fieldState.error}
                        helperText={fieldState.error?.message || ''}
                        InputLabelProps={{
                          required: false,
                          shrink: false
                        }}
                        disabled={paymentInfoSubmitted}
                        inputProps={{
                          ...params.inputProps,
                          'data-recurly': 'country'
                        }}
                      />
                    )}
                  />)}
              />

            </Grid>
            <Grid item xs={12} mb={1}>
              <Controller
                name='address1'
                control={paymentForm.control}
                rules={{ required: 'Country is required' }}
                render={({ field, fieldState }) => (
                  <TextField
                    placeholder='Address 1'
                    variant='standard'
                    fullWidth
                    sx={textFieldStyle}
                    required
                    helperText={fieldState.error?.message || ''}
                    InputLabelProps={{ required: false }}
                    disabled={paymentInfoSubmitted}
                    inputProps={{
                      'data-recurly': 'address1'
                    }}
                    error={fieldState.error}
                    {...handleFieldChange(field)}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} mb={1}>
              <Controller
                name='address2'
                control={paymentForm.control}
                render={({ field, fieldState }) => (
                  <TextField
                    placeholder='Address 2'
                    variant='standard'
                    fullWidth
                    sx={textFieldStyle}
                    required
                    InputLabelProps={{ required: false }}
                    disabled={paymentInfoSubmitted}
                    inputProps={{
                      'data-recurly': 'address2'
                    }}
                    error={fieldState.error}
                    {...handleFieldChange(field)}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} mb={1}>
              <Controller
                name='city'
                control={paymentForm.control}
                render={({ field, fieldState }) => (
                  <TextField
                    placeholder='City'
                    variant='standard'
                    fullWidth
                    required
                    InputLabelProps={{ required: false }}
                    sx={textFieldStyle}
                    disabled={paymentInfoSubmitted}
                    inputProps={{
                      'data-recurly': 'city'
                    }}
                    error={fieldState.error}
                    {...handleFieldChange(field)}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} lg={6} mb={1}>
              <Controller
                name='state'
                control={paymentForm.control}
                render={({ field, fieldState }) => (
                  <TextField
                    placeholder='State'
                    variant='standard'
                    fullWidth
                    required
                    InputLabelProps={{ required: false }}
                    sx={textFieldStyle}
                    disabled={paymentInfoSubmitted}
                    inputProps={{
                      'data-recurly': 'state'
                    }}
                    error={fieldState.error}
                    {...handleFieldChange(field)}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} lg={6}>
              <Controller
                name='postcode'
                control={paymentForm.control}
                render={({ field, fieldState }) => (
                  <TextField
                    placeholder='Postal Code'
                    variant='standard'
                    fullWidth
                    required
                    sx={textFieldStyle}
                    disabled={paymentInfoSubmitted}
                    inputProps={{
                      'data-recurly': 'postal_code'
                    }}
                    error={fieldState.error}
                    {...handleFieldChange(field)}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sx={{ mt: 1 }}>
              <ActionButton
                label='Complete payment'
                variant='primary'
                loading={paymentInfoSubmitted}
                className={classes.submitButton}
                disabled={!allValid || paymentInfoSubmitted}
                onClick={handleSubmit}
              />
            </Grid>
          </Grid>

        </>
      </Collapse>
    </Paper>
  )
}
